import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Textarea,
  RadioGroup,
  Radio,
  useColorModeValue,
  useToast,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { MdGavel } from 'react-icons/md'
import { useResolveConflict } from 'queries/reconciliation.query'

export function ResolveConflictModal({ isOpen, onClose, order }) {
  const [action, setAction] = useState('force_refund')
  const [notes, setNotes] = useState('')
  const toast = useToast()
  const resolveMutation = useResolveConflict()

  const cardBg = useColorModeValue('gray.50', 'navy.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')

  if (!order) return null

  const handleResolve = async () => {
    if (!notes.trim()) {
      toast({
        title: 'Audit Notes Required',
        description: 'Please describe the justification and operational reason for this resolution.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    try {
      const res = await resolveMutation.mutateAsync({
        orderId: order.id,
        action,
        notes: notes.trim(),
      })

      toast({
        title: 'Conflict Resolved Successfully',
        description: res?.message || `Order #${order.order_number} resolved.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      onClose()
      setNotes('')
    } catch (err) {
      toast({
        title: 'Resolution Failed',
        description: err?.response?.data?.message || err.message || 'Error resolving conflict.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} py={4}>
          <HStack spacing={2.5}>
            <Icon as={MdGavel} color="purple.500" w="24px" height="24px" />
            <Text fontSize="lg" fontWeight="bold">
              Resolve Order Inventory & Payment Conflict
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            {/* Conflict Order Summary */}
            <Box p={4} borderRadius="14px" bg={cardBg} border="1px solid" borderColor={borderColor} fontSize="xs">
              <HStack justify="space-between" mb={2}>
                <Text color="gray.500">Order Number:</Text>
                <Text fontWeight="bold" fontFamily="mono" fontSize="sm">
                  {order.order_number}
                </Text>
              </HStack>
              <HStack justify="space-between" mb={2}>
                <Text color="gray.500">Customer & Amount:</Text>
                <Text fontWeight="semibold">
                  {order.customer_name} • €{Number(order.total_amount).toFixed(2)}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.500">Current Status:</Text>
                <Badge colorScheme="red">{order.payment_status}</Badge>
              </HStack>
            </Box>

            {/* Resolution Options */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }} mb={2.5}>
                Select Resolution Strategy:
              </Text>
              <RadioGroup value={action} onChange={setAction}>
                <VStack spacing={3} align="stretch">
                  <Box
                    p={3.5}
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={action === 'force_refund' ? 'purple.400' : borderColor}
                    bg={action === 'force_refund' ? 'purple.50' : 'transparent'}
                    _dark={{ bg: action === 'force_refund' ? 'purple.900' : 'transparent' }}
                  >
                    <Radio value="force_refund" colorScheme="purple">
                      <VStack align="flex-start" spacing={0.5} ml={2}>
                        <Text fontSize="xs" fontWeight="bold">
                          Force Immediate Gateway Refund (Recommended)
                        </Text>
                        <Text fontSize="11px" color="gray.500">
                          Issues full gateway refund to customer and marks order as REFUNDED. Protects inventory integrity.
                        </Text>
                      </VStack>
                    </Radio>
                  </Box>

                  <Box
                    p={3.5}
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={action === 'mark_paid' ? 'green.400' : borderColor}
                    bg={action === 'mark_paid' ? 'green.50' : 'transparent'}
                    _dark={{ bg: action === 'mark_paid' ? 'green.900' : 'transparent' }}
                  >
                    <Radio value="mark_paid" colorScheme="green">
                      <VStack align="flex-start" spacing={0.5} ml={2}>
                        <Text fontSize="xs" fontWeight="bold">
                          Override & Mark as Paid
                        </Text>
                        <Text fontSize="11px" color="gray.500">
                          Confirms the order and issues tickets if seat inventory was successfully retained.
                        </Text>
                      </VStack>
                    </Radio>
                  </Box>

                  <Box
                    p={3.5}
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={action === 'cancel_and_release' ? 'red.400' : borderColor}
                    bg={action === 'cancel_and_release' ? 'red.50' : 'transparent'}
                    _dark={{ bg: action === 'cancel_and_release' ? 'red.900' : 'transparent' }}
                  >
                    <Radio value="cancel_and_release" colorScheme="red">
                      <VStack align="flex-start" spacing={0.5} ml={2}>
                        <Text fontSize="xs" fontWeight="bold">
                          Cancel Order & Release Seats
                        </Text>
                        <Text fontSize="11px" color="gray.500">
                          Cancels the order, marks as CANCELLED, and releases any held seats back to the venue map.
                        </Text>
                      </VStack>
                    </Radio>
                  </Box>
                </VStack>
              </RadioGroup>
            </Box>

            {/* Audit Justification */}
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }} mb={1.5}>
                Audit Justification / Resolution Notes * (Required):
              </Text>
              <Textarea
                required
                placeholder="State the audit reason (e.g. late capture confirmed after inventory hold expired, customer agreed to refund)..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                borderRadius="12px"
                fontSize="xs"
                rows={3}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor} py={3}>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="10px">
            Dismiss
          </Button>
          <Button
            colorScheme="purple"
            borderRadius="10px"
            onClick={handleResolve}
            isLoading={resolveMutation.isPending}
            loadingText="Resolving..."
            px={6}
          >
            Apply Resolution
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
